import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import {
    Alert,
    Button,
    ColorIndicator,
    ConfirmModal,
    DataTable,
    Icon,
    IconButton,
    Loading,
    Spacer,
    StyleFunction,
    Surface,
    useAuth,
    useModal,
    usePagination,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQApprovalsQuery,
    useApprovalDeleteMutation,
    useApprovalsQuery,
} from "graphql-sdk";
import { ApprovalType, formatDateTime } from "lib";
import { AvailableLocale, useTranslation } from "locales";
import React, { useCallback, useEffect } from "react";
import { View } from "react-native";
import { useHandleMutationError } from "../../../hooks";
import { RootStackParamList } from "../../../navigation";
import { useAdminSession } from "../../../session";
import { AdminContainer } from "../../container";

export function ApprovalListScreen() {
    const auth = useAuth();
    auth.enforce(["merchant.approval"]);

    const styles = useThemedStyle(styleFunc);
    const [{ locale }] = useAdminSession(["locale"]);
    const theme = useTheme();
    const [t] = useTranslation();
    const { render } = useModal();
    const { navigate } = useNavigation();
    const route = useRoute<RouteProp<RootStackParamList, "APPROVALS">>();

    const [approvalDelete] = useApprovalDeleteMutation();
    const { handleMutationError } = useHandleMutationError();

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "type",
        initialSortDirection: "ASC",
    });

    const { data, loading, error, refetch } = useApprovalsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
        },
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (route.params?.refetch) {
            refetch();
        }
    }, [refetch, route.params?.refetch]);

    const handleEdit = useCallback(
        (id: string) => {
            navigate("APPROVAL_EDIT", {
                id,
            });
        },
        [navigate]
    );

    const handleDelete = useCallback(
        async (id: string) => {
            if (
                !(await render(onClose => (
                    <ConfirmModal
                        headerText={t(
                            "backoffice.approval.delete",
                            "Delete approval?"
                        )}
                        bodyText={t(
                            "backoffice.approval.delete_explain",
                            "Are you sure, you want to delete the approval?"
                        )}
                        onClose={onClose}
                    />
                )))
            ) {
                return;
            }

            await handleMutationError(
                async () =>
                    await approvalDelete({
                        variables: {
                            id: id,
                        },
                    }),
                t("backoffice.approval.deleted", "Approval deleted"),
                async () => {
                    await refetch();
                }
            );
        },
        [approvalDelete, handleMutationError, refetch, render, t]
    );

    const translateType = useCallback(
        (type: ApprovalType) => {
            switch (type) {
                case "DISCOUNT":
                    return t(
                        "backoffice.approval.types.DISCOUNT",
                        "Add discount"
                    );
                case "OPEN_CASH_DRAWER":
                    return t(
                        "backoffice.approval.types.OPEN_CASH_DRAWER",
                        "Open cashdrawer"
                    );
                case "REFUND":
                    return t(
                        "backoffice.approval.types.REFUND",
                        "Refund amount"
                    );
                case "RETURN":
                    return t(
                        "backoffice.approval.types.RETURN",
                        "Return products"
                    );
            }
        },
        [t]
    );

    const renderCmpEventRow = useCallback(
        (item: GQApprovalsQuery["approvals"]["data"][0], rowIndex) => (
            <DataTable.Row
                style={rowIndex % 2 ? theme.styles.oddRow : undefined}
                onPress={() => handleEdit(item.id)}
                key={`approvalId_${item.id}`}
            >
                <DataTable.Cell style={styles.iconColumn}>
                    <ColorIndicator
                        status={item.active ? "success" : "warning"}
                    />
                </DataTable.Cell>
                <DataTable.Cell>{translateType(item.type)}</DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.createdAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.updatedAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell style={styles.iconColumn} numeric>
                    <IconButton
                        color={theme.colors.secondary}
                        name="edit"
                        onPress={() => handleEdit(item.id)}
                        testID={"edit:" + item.id}
                    />
                    <IconButton
                        color={theme.colors.secondary}
                        name="delete"
                        onPress={() => handleDelete(item.id)}
                        testID={"delete:" + item.id}
                    />
                </DataTable.Cell>
            </DataTable.Row>
        ),
        [
            handleEdit,
            handleDelete,
            locale,
            styles.iconColumn,
            theme.colors.secondary,
            theme.styles.oddRow,
            translateType,
        ]
    );

    if (error) {
        return (
            <Alert type="error">
                {t(
                    "backoffice.error.from_server",
                    "There was an error: {{errorText}}",
                    {
                        errorText: error?.message,
                    }
                )}
            </Alert>
        );
    }

    return (
        <AdminContainer>
            <View style={[theme.styles.row, styles.container]}>
                <Button
                    onPress={() => {
                        navigate("APPROVAL_CREATE");
                    }}
                    style={styles.button}
                >
                    {t("backoffice.common.create", "Create")}
                </Button>
            </View>
            <Spacer space={2} />
            <Surface>
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "active" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("active")}
                            style={styles.iconColumn}
                        >
                            {t("backoffice.common.active", "Active")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "type" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("type")}
                        >
                            {t("backoffice.approval.type", "Type")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "createdAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("createdAt")}
                        >
                            {t("common.created_at", "Created at")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "updatedAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("updatedAt")}
                        >
                            {t("common.updated_at", "Updated at")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.iconColumn} numeric>
                            <Icon name="sort" color={theme.colors.black} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {loading ? (
                        <Loading />
                    ) : data.approvals.data.length === 0 ? (
                        <DataTable.Row style={theme.styles.oddRow}>
                            <DataTable.Cell>
                                {t(
                                    "backoffice.approval.no_data",
                                    "No approvals."
                                )}
                            </DataTable.Cell>
                        </DataTable.Row>
                    ) : (
                        data?.approvals.data.map(renderCmpEventRow)
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.approvals.pagination.pages}
                        itemCount={data?.approvals.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = () => ({
    container: {
        justifyContent: "flex-end",
        flexWrap: "wrap",
    },
    iconColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 60 },
});
